import { FolderOpen } from "lucide-react-native";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { ScrollView } from "react-native";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Badge,
  BadgeText,
  Box,
  Button,
  ButtonText,
  ErrorWithRetry,
  Heading,
  HStack,
  Pressable,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import { Folder, FolderType } from "@/types/resources";

interface MoveToModalProps {
  currentFolderId?: string | null;
  excludeSelfId?: string;
  isOpen: boolean;
  itemName: string;
  onClose: () => void;
  onMove: (targetFolderId: string | null) => Promise<boolean>;
  type: FolderType;
}

interface FolderTreeNode extends Folder {
  children: FolderTreeNode[];
}

const buildTree = (
  folders: Folder[],
  parentId: string | null
): FolderTreeNode[] => {
  return folders
    .filter((f) => f.parent === parentId)
    .map((f) => ({
      ...f,
      children: buildTree(folders, f.id),
    }));
};

const flattenTree = (nodes: FolderTreeNode[], excludeId?: string): string[] => {
  const result: string[] = [];
  for (const node of nodes) {
    if (node.id === excludeId) continue;
    result.push(node.id);
    result.push(...flattenTree(node.children, excludeId));
  }
  return result;
};

interface FolderRowProps {
  depth: number;
  excludeId?: string;
  node: FolderTreeNode;
  onSelect: (id: string | null) => void;
  selectedId: string | null | undefined;
}

const FolderRow: FC<FolderRowProps> = ({
  depth,
  excludeId,
  node,
  onSelect,
  selectedId,
}) => {
  if (node.id === excludeId) return null;

  const isSelected = selectedId === node.id;

  return (
    <VStack>
      <Pressable onPress={() => onSelect(node.id)}>
        <HStack
          className={`items-center gap-2 py-2.5 px-3 rounded-xl ${isSelected ? "bg-primary-50" : "hover:bg-background-50"}`}
          style={{ paddingLeft: 12 + depth * 16 }}
        >
          <FolderOpen
            color={
              isSelected
                ? "rgb(var(--color-primary-600))"
                : "rgb(var(--color-typography-500))"
            }
            size={16}
          />
          <Text
            className={`text-sm ${isSelected ? "text-primary-700 font-medium" : "text-typography-700"}`}
          >
            {node.name}
          </Text>
        </HStack>
      </Pressable>
      {node.children.map((child) => (
        <FolderRow
          depth={depth + 1}
          excludeId={excludeId}
          key={child.id}
          node={child}
          onSelect={onSelect}
          selectedId={selectedId}
        />
      ))}
    </VStack>
  );
};

const MoveToModal: FC<MoveToModalProps> = ({
  currentFolderId,
  excludeSelfId,
  isOpen,
  itemName,
  onClose,
  onMove,
  type,
}) => {
  const { i18n } = useLocalization();
  const [selectedId, setSelectedId] = useState<string | null>(
    currentFolderId ?? null
  );
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);

  const { error, folders, loading, refresh } = API.Folders.useFolders(type);

  const tree = useMemo(() => buildTree(folders, null), [folders]);

  const navigableIds = useMemo<(string | null)[]>(
    () => [null, ...flattenTree(tree, excludeSelfId)],
    [tree, excludeSelfId]
  );

  const isRootSelected = selectedId === null;

  const rootLabel =
    type === "studies"
      ? i18n.t("dashboard.activeStudies")
      : i18n.t("dashboard.managePanels");

  const handleConfirm = useCallback(async () => {
    setMoveError(null);
    setMoving(true);
    const success = await onMove(selectedId);
    setMoving(false);
    if (success) {
      onClose();
    } else {
      setMoveError(i18n.t("misc.errorOccurred"));
    }
  }, [selectedId, onMove, onClose, i18n]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedId((prev) => {
          const currentIndex = navigableIds.indexOf(prev);
          const nextIndex =
            e.key === "ArrowDown"
              ? Math.min(currentIndex + 1, navigableIds.length - 1)
              : Math.max(currentIndex - 1, 0);
          return navigableIds[nextIndex] ?? null;
        });
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (!moving && !loading && selectedId !== (currentFolderId ?? null))
          handleConfirm();
      }
    };

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [
    isOpen,
    navigableIds,
    moving,
    loading,
    handleConfirm,
    selectedId,
    currentFolderId,
  ]);

  return (
    <AlertDialog isOpen={isOpen} onClose={onClose}>
      <AlertDialogBackdrop />
      <AlertDialogContent className="gap-6 max-w-md">
        <AlertDialogHeader>
          <VStack className="gap-1">
            <Heading numberOfLines={1} size="lg">
              {i18n.t("folders.moveItem", { name: itemName })}
            </Heading>
            <HStack className="items-center gap-1.5 flex-wrap">
              <Text className="text-sm text-typography-500">
                {i18n.t("folders.currentLocation")}:
              </Text>
              <Badge
                action="muted"
                className="px-2 py-0.5 rounded-full"
                variant="solid"
              >
                <BadgeText className="text-xs font-medium normal-case">
                  {currentFolderId
                    ? (folders.find((f) => f.id === currentFolderId)?.name ??
                      rootLabel)
                    : rootLabel}
                </BadgeText>
              </Badge>
            </HStack>
          </VStack>
        </AlertDialogHeader>
        <AlertDialogBody>
          {loading ? (
            <VStack className="gap-2">
              {[1, 2, 3].map((i) => (
                <Skeleton className="h-9 w-full rounded-xl" key={i} />
              ))}
            </VStack>
          ) : error ? (
            <ErrorWithRetry
              error={error}
              onRetry={refresh}
              title={i18n.t("folders.errorLoadingFolders")}
            />
          ) : (
            <ScrollView
              className="max-h-72"
              showsVerticalScrollIndicator={false}
            >
              <VStack className="gap-0.5">
                {/* Root option */}
                <Pressable onPress={() => setSelectedId(null)}>
                  <HStack
                    className={`items-center gap-2 py-2.5 px-3 rounded-xl ${isRootSelected ? "bg-primary-50" : "hover:bg-background-50"}`}
                  >
                    <Box className="w-4 h-4 items-center justify-center">
                      <Box
                        className={`w-3 h-3 rounded-full border-2 ${isRootSelected ? "border-primary-600 bg-primary-600" : "border-typography-400"}`}
                      />
                    </Box>
                    <Text
                      className={`text-sm ${isRootSelected ? "text-primary-700 font-medium" : "text-typography-700"}`}
                    >
                      {rootLabel}
                    </Text>
                  </HStack>
                </Pressable>

                {tree.map((node) => (
                  <FolderRow
                    depth={0}
                    excludeId={excludeSelfId}
                    key={node.id}
                    node={node}
                    onSelect={setSelectedId}
                    selectedId={selectedId}
                  />
                ))}

                {folders.length === 0 && (
                  <Text className="text-sm text-typography-500 text-center py-4">
                    {i18n.t("folders.noFoldersYet")}
                  </Text>
                )}
              </VStack>
            </ScrollView>
          )}
        </AlertDialogBody>
        <AlertDialogFooter>
          <VStack className="gap-2 w-full">
            <HStack className="gap-3 justify-end">
              <Button
                className="rounded-xl"
                isDisabled={moving}
                onPress={onClose}
                variant="outline"
              >
                <ButtonText>{i18n.t("misc.cancel")}</ButtonText>
              </Button>
              <Button
                className="rounded-xl"
                isDisabled={
                  moving || loading || selectedId === (currentFolderId ?? null)
                }
                onPress={handleConfirm}
              >
                <ButtonText>
                  {moving ? i18n.t("misc.moving") : i18n.t("misc.move")}
                </ButtonText>
              </Button>
            </HStack>
            {moveError && (
              <Text className="text-error-600 text-sm text-right">
                {moveError}
              </Text>
            )}
          </VStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default MoveToModal;
